import React, { useMemo, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, Users, Phone, User, StickyNote, ChevronRight, Check, Clock, Sparkles } from "lucide-react";
import { format, addDays, startOfDay, parseISO } from "date-fns";
import { pt } from "date-fns/locale";

import Brand from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/de5613cb-7640-482a-a783-f5168d10ceb8/images/b395ac400e5fc7bca2bb7462d4bca51d9d63a3ec1582a1377c1b6accb3542e7d.png";

// JS Date.getDay(): Sunday=0, Monday=1, Tuesday=2 ... Closed on Sun/Mon/Tue.
const JS_CLOSED_DAYS = new Set([0, 1, 2]);

const STATUS_META = {
  available: { label: "disponível",          cls: "text-[#34C759]", dot: "bg-[#34C759]" },
  low:       { label: "últimos lugares",    cls: "text-[#FF9500]", dot: "bg-[#FF9500]" },
  full:      { label: "esgotado",            cls: "text-[#FF3B30]", dot: "bg-[#FF3B30]" },
  closed:    { label: "sem disponibilidade", cls: "text-[#6b6763]", dot: "bg-[#6b6763]" },
};

export default function PublicReservation() {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [partySize, setPartySize] = useState("2");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [nextDays, setNextDays] = useState([]);
  const [closedDates, setClosedDates] = useState(new Set());

  const minDate = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addDays(new Date(), 90), []);

  // Fetch availability for selected date
  useEffect(() => {
    if (!date) { setAvailability(null); return; }
    let cancelled = false;
    setLoadingAvail(true);
    api.get("/availability", { params: { date: format(date, "yyyy-MM-dd") } })
      .then(({ data }) => { if (!cancelled) setAvailability(data); })
      .catch(() => { if (!cancelled) setAvailability(null); })
      .finally(() => { if (!cancelled) setLoadingAvail(false); });
    return () => { cancelled = true; };
  }, [date]);

  // Fetch next-days widget data (once)
  useEffect(() => {
    let cancelled = false;
    api.get("/availability/next", { params: { days: 7 } })
      .then(({ data }) => { if (!cancelled) setNextDays(data.days || []); })
      .catch((err) => { console.debug("[next-days] failed:", err?.message); });
    api.get("/availability/closed-dates")
      .then(({ data }) => { if (!cancelled) setClosedDates(new Set(data.dates || [])); })
      .catch((err) => { console.debug("[closed-dates] failed:", err?.message); });
    return () => { cancelled = true; };
  }, []);

  // Clear chosen time if it becomes unavailable
  useEffect(() => {
    if (!availability || !time) return;
    const s = availability.slots.find((sl) => sl.time === time);
    if (s && (s.status === "full" || s.status === "closed")) setTime("");
  }, [availability, time]);

  const partySizeNum = Number(partySize);
  const selectedSlot = availability?.slots.find((s) => s.time === time);
  const exceedsCapacity = selectedSlot && !availability?.is_closed_day
    ? (selectedSlot.remaining < partySizeNum)
    : false;

  const canSubmit =
    name.trim().length >= 2 &&
    phone.trim().length >= 6 &&
    date && time && partySize &&
    !availability?.is_closed_day &&
    !exceedsCapacity;

  const pickDay = useCallback((dateStr) => {
    const d = parseISO(dateStr);
    setDate(d);
    setTime("");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Preencha todos os campos e escolha um horário disponível.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        phone: phone.trim(),
        date: format(date, "yyyy-MM-dd"),
        time,
        party_size: Number(partySize),
        notes: notes.trim(),
      };
      const { data } = await api.post("/reservations", payload);
      setSuccess(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao criar reserva.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return <SuccessView reservation={success} onReset={() => { setSuccess(null); setDate(null); setTime(""); setName(""); setPhone(""); setNotes(""); setPartySize("2"); }} />;
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Hero background */}
      <div className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
      </div>

      {/* Top nav */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 lg:px-24 pt-8">
        <Brand size="md" />
        <Link
          to="/admin/login"
          data-testid="nav-admin-link"
          className="text-xs uppercase tracking-[0.2em] text-[#A19D98] hover:text-[#D45A2C] transition-colors"
        >
          admin
        </Link>
      </header>

      {/* Main */}
      <main className="relative z-10 px-6 md:px-12 lg:px-24 py-16 md:py-24 grid lg:grid-cols-12 gap-12 items-start">
        {/* Left — editorial */}
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-5 pt-4"
        >
          <span className="accent-rule" />
          <p className="mt-6 text-xs uppercase tracking-[0.28em] text-[#A19D98]">
            Reserva de Mesa · Parede, Cascais
          </p>
          <h1 className="font-poster text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#F4EFEB] mt-5">
            Reserve a sua<br/>
            mesa no <span className="text-[#D45A2C]">LAMUSIK</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-[#A19D98] leading-relaxed max-w-md">
            A reserva fica <span className="text-[#F4EFEB]">pendente</span> até receber a <span className="text-[#F4EFEB]">confirmação por SMS</span>. Para reservas de grupos superiores a 12 pessoas, entrar em contacto telefónico com o restaurante, <a href="tel:+351912383745" className="text-[#F4EFEB] hover:text-[#D45A2C] transition-colors whitespace-nowrap">+351 912 383 745</a>.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="https://drive.google.com/file/d/1JAZ5YEMeTmPAsondXD1dr9WCBv65M3pM/view"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-ver-menu"
              className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.3em] border border-[#D45A2C] text-[#D45A2C] hover:bg-[#D45A2C] hover:text-white transition-colors"
            >
              <Sparkles className="h-4 w-4" /> Ver menu
            </a>
            <Link
              to="/sobre"
              data-testid="btn-sobre-restaurante"
              className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.3em] border border-white/15 text-[#F4EFEB] hover:border-[#D45A2C] hover:text-[#D45A2C] transition-colors"
            >
              Sobre o restaurante
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
            {[
              { n: "01", t: "Escolha" },
              { n: "02", t: "Reserva" },
              { n: "03", t: "SMS" },
            ].map((s) => (
              <div key={s.n}>
                <div className="font-display text-3xl text-[#D45A2C]">{s.n}</div>
                <div className="text-xs uppercase tracking-widest text-[#A19D98] mt-2">{s.t}</div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Right — form card */}
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
          className="lg:col-span-7 lg:col-start-7"
        >
          {/* Next-days widget */}
          {nextDays.length > 0 && (
            <NextDaysWidget
              days={nextDays}
              selectedDate={date ? format(date, "yyyy-MM-dd") : null}
              onPick={pickDay}
            />
          )}

          <form
            onSubmit={submit}
            data-testid="reservation-form"
            className="glass p-8 md:p-12 rounded-none"
          >
            <h2 className="font-poster text-2xl md:text-3xl text-[#F4EFEB] mb-8 tracking-wide">Reservar Mesa</h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Name */}
              <Field label="Nome" icon={<User className="h-3.5 w-3.5" />}>
                <Input
                  data-testid="input-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="O seu nome"
                  className="lm-input"
                />
              </Field>

              {/* Phone */}
              <Field label="Telemóvel" icon={<Phone className="h-3.5 w-3.5" />}>
                <Input
                  data-testid="input-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+351 ..."
                  inputMode="tel"
                  className="lm-input"
                />
              </Field>

              {/* Date */}
              <Field label="Data" icon={<CalendarIcon className="h-3.5 w-3.5" />}>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="btn-date-picker"
                      className="lm-input text-left h-[46px] flex items-center"
                    >
                      {date ? format(date, "d MMM yyyy", { locale: pt }) : <span className="text-[#6b6763]">Escolher data</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#0E0E0E] border-white/10" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) =>
                        d < minDate ||
                        d > maxDate ||
                        JS_CLOSED_DAYS.has(d.getDay()) ||
                        closedDates.has(format(d, "yyyy-MM-dd"))
                      }
                      locale={pt}
                      initialFocus
                      className="bg-[#0E0E0E] text-[#F4EFEB]"
                    />
                  </PopoverContent>
                </Popover>
              </Field>

              {/* Party size */}
              <Field label="Pessoas" icon={<Users className="h-3.5 w-3.5" />}>
                <Select value={partySize} onValueChange={setPartySize}>
                  <SelectTrigger data-testid="select-party-size" className="lm-input h-[46px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0E0E0E] border-white/10 text-[#F4EFEB]">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)} className="focus:bg-[#1A1A1A] focus:text-[#D45A2C]">
                        {n} {n === 1 ? "pessoa" : "pessoas"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {/* Time slots */}
            <div className="mt-8">
              <div className="flex items-end justify-between">
                <Label className="text-xs uppercase tracking-[0.22em] text-[#F4EFEB] flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5" /> Horário
                </Label>
                {date && availability && (
                  <span className="text-[10px] uppercase tracking-widest text-[#6b6763]" data-testid="capacity-hint">
                    {loadingAvail ? "a verificar..." : `até ${availability.capacity} pessoas / hora`}
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {(availability?.slots || fallbackSlots()).map((slot) => {
                  const t = slot.time;
                  const status = slot.status || "available";
                  const isFull = status === "full" || status === "closed";
                  const isLow = status === "low";
                  const selected = t === time;
                  const disabled = isFull || !date;
                  const meta = STATUS_META[status];
                  return (
                    <button
                      type="button"
                      key={t}
                      data-testid={`timeslot-${t}`}
                      disabled={disabled}
                      onClick={() => !disabled && setTime(t)}
                      className={`group relative flex flex-col items-start gap-1.5 px-4 py-3 border transition-colors text-left ${
                        selected
                          ? "border-[#D45A2C] bg-[#D45A2C]/10 text-[#D45A2C]"
                          : isFull
                          ? "border-white/5 bg-black/30 text-[#6b6763] cursor-not-allowed"
                          : isLow
                          ? "border-[#FF9500]/30 text-[#F4EFEB] hover:border-[#FF9500]/60"
                          : "border-white/10 text-[#A19D98] hover:border-white/30 hover:text-[#F4EFEB]"
                      }`}
                    >
                      <span className="text-lg font-display tracking-tight">{t}</span>
                      {date && availability ? (
                        <span className={`text-[10px] uppercase tracking-widest flex items-center gap-1.5 ${meta.cls}`}>
                          <span className={`inline-block h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                          {meta.label}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-[#6b6763]">escolha uma data</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {selectedSlot && exceedsCapacity && (
                <p className="mt-3 text-xs text-[#FF9500]" data-testid="capacity-warning">
                  Restam apenas {selectedSlot.remaining} lugar(es) às {selectedSlot.time}. Reduza o nº de pessoas ou escolha outro horário.
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="mt-8">
              <Label className="text-xs uppercase tracking-[0.22em] text-[#F4EFEB] flex items-center gap-2">
                <StickyNote className="h-3.5 w-3.5" /> Observações <span className="normal-case tracking-normal text-[#6b6763]">(opcional)</span>
              </Label>
              <Textarea
                data-testid="input-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Aniversário, alergias, pedidos especiais..."
                rows={3}
                className="mt-3 lm-input resize-none"
              />
            </div>

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-[#A19D98] max-w-xs">
                Ao reservar, concorda em receber um SMS de confirmação no número indicado.
              </p>
              <Button
                type="submit"
                data-testid="btn-submit-reservation"
                disabled={!canSubmit || submitting}
                className="bg-[#D45A2C] hover:bg-[#B84B23] text-white rounded-none px-8 py-6 text-xs uppercase tracking-[0.28em] font-semibold disabled:opacity-40"
              >
                {submitting ? "A enviar..." : <>Pedir reserva <ChevronRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          </form>
        </motion.section>
      </main>

      <footer className="relative z-10 px-6 md:px-12 lg:px-24 py-10 text-xs uppercase tracking-[0.25em] text-[#6b6763] flex items-center justify-between">
        <span>© {new Date().getFullYear()} LAMUSIK</span>
        <span>Parede, Cascais</span>
      </footer>
    </div>
  );
}

function fallbackSlots() {
  return ["19:30", "20:00", "20:30", "21:00", "21:30", "22:00"].map((t) => ({ time: t, status: null }));
}

function Field({ label, icon, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.22em] text-[#F4EFEB] flex items-center gap-2 mb-3">
        {icon} {label}
      </Label>
      {children}
    </div>
  );
}

const WEEKDAY_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

function NextDaysWidget({ days, selectedDate, onPick }) {
  return (
    <div className="mb-6" data-testid="next-days-widget">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-[#D45A2C]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#A19D98]">próximos dias</span>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {days.map((d) => {
          const date = parseISO(d.date);
          const wd = WEEKDAY_SHORT[date.getDay()];
          const meta = STATUS_META[d.status] || STATUS_META.available;
          const selected = selectedDate === d.date;
          const full = d.status === "full";
          return (
            <button
              key={d.date}
              type="button"
              data-testid={`next-day-${d.date}`}
              disabled={full}
              onClick={() => !full && onPick(d.date)}
              className={`group flex flex-col items-center justify-center gap-1 py-3 border transition-colors ${
                selected
                  ? "border-[#D45A2C] bg-[#D45A2C]/10"
                  : full
                  ? "border-white/5 bg-black/20 cursor-not-allowed"
                  : "border-white/10 hover:border-white/30 bg-[#0A0A0A]"
              }`}
            >
              <span className={`text-[10px] uppercase tracking-widest ${selected ? "text-[#D45A2C]" : "text-[#6b6763]"}`}>{wd}</span>
              <span className={`font-display text-2xl ${selected ? "text-[#D45A2C]" : full ? "text-[#6b6763] line-through" : "text-[#F4EFEB]"}`}>
                {format(date, "d")}
              </span>
              <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest ${meta.cls}`}>
                <span className={`inline-block h-1 w-1 rounded-full ${meta.dot}`} />
                {meta.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SuccessView({ reservation, onReset }) {
  return (
    <div className="relative min-h-screen flex items-center justify-center px-6">
      <div className="absolute inset-0 -z-10 bg-black" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-xl w-full glass p-10 md:p-14 text-center"
        data-testid="reservation-success"
      >
        <div className="mx-auto w-12 h-12 rounded-full bg-[#D45A2C]/15 border border-[#D45A2C]/30 flex items-center justify-center">
          <Check className="text-[#D45A2C]" />
        </div>
        <span className="accent-rule mt-8 inline-block" />
        <h2 className="font-display text-4xl md:text-5xl text-[#F4EFEB] mt-6">Pedido recebido.</h2>
        <p className="mt-4 text-[#A19D98]">
          Olá <span className="text-[#F4EFEB]">{reservation.name}</span>, a sua reserva está <span className="text-[#FF9500]">pendente</span>. Vamos enviar um SMS para <span className="text-[#F4EFEB]">{reservation.phone}</span> assim que confirmarmos.
        </p>

        <dl className="mt-10 grid grid-cols-3 gap-4 text-left">
          <div className="border-t border-white/10 pt-4">
            <dt className="text-[10px] uppercase tracking-widest text-[#6b6763]">Data</dt>
            <dd className="text-[#F4EFEB] mt-1">{reservation.date}</dd>
          </div>
          <div className="border-t border-white/10 pt-4">
            <dt className="text-[10px] uppercase tracking-widest text-[#6b6763]">Hora</dt>
            <dd className="text-[#F4EFEB] mt-1">{reservation.time}</dd>
          </div>
          <div className="border-t border-white/10 pt-4">
            <dt className="text-[10px] uppercase tracking-widest text-[#6b6763]">Pessoas</dt>
            <dd className="text-[#F4EFEB] mt-1">{reservation.party_size}</dd>
          </div>
        </dl>

        <button
          onClick={onReset}
          data-testid="btn-new-reservation"
          className="mt-10 text-xs uppercase tracking-[0.28em] text-[#D45A2C] hover:text-[#F4EFEB] transition-colors"
        >
          Fazer outra reserva →
        </button>
      </motion.div>
    </div>
  );
}
